import React, { useState, useEffect } from 'react';
import { MessageSquare, Clock, AlertCircle, Eye, Archive, Bell, Check } from 'lucide-react';
import { dataHelpers } from '../../lib/supabase';
import Card from '../../components/ui/Card';
import toast from 'react-hot-toast';

const ParentMessages = ({ parentId, onMessageRead }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (parentId) {
      loadMessages();
    }
  }, [parentId]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await dataHelpers.getMessagesByRecipient(parentId);
      setMessages(data);
      
      // Contar mensagens não lidas
      const unread = data.filter(msg => !msg.read_at).length;
      setUnreadCount(unread);
      
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };
  const handleMarkAsRead = async (messageId) => {
    try {
      await dataHelpers.markMessageAsRead(messageId, parentId);
      
      // Atualizar estado local
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId 
          ? { ...msg, read_at: new Date().toISOString() }
          : msg
      ));
      
      // Atualizar contador
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Notificar componente pai para atualizar badge
      if (onMessageRead) {
        onMessageRead();
      }
      
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast.error('Erro ao marcar mensagem como lida');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Hoje';
    } else if (diffDays === 2) {
      return 'Ontem';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} dias atrás`;
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'high':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <Bell className="w-4 h-4 text-blue-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500 bg-red-50';
      case 'high': return 'border-l-orange-500 bg-orange-50';
      case 'normal': return 'border-l-blue-500 bg-blue-50';
      case 'low': return 'border-l-gray-500 bg-gray-50';
      default: return 'border-l-blue-500 bg-blue-50';
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com contador */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MessageSquare className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Mensagens da Cantina</h2>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
      </div>

      {/* Lista de mensagens */}
      <div className="space-y-4">
        {messages.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma mensagem ainda</p>
            <p className="text-sm text-gray-400 mt-1">
              As mensagens da cantina aparecerão aqui
            </p>
          </Card>
        ) : (
          messages.map((message) => {
            const isUnread = !message.read_at;
            
            return (
              <Card 
                key={message.id} 
                className={`p-4 border-l-4 cursor-pointer transition-all hover:shadow-md ${
                  isUnread ? `${getPriorityColor(message.priority || 'normal')} border-l-4` : 'bg-white border-l-gray-300'
                }`}
                onClick={() => {
                  setSelectedMessage(message);
                  if (isUnread) {
                    handleMarkAsRead(message.id);
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getPriorityIcon(message.priority || 'normal')}
                      <h3 className={`font-semibold ${isUnread ? 'text-gray-900' : 'text-gray-700'}`}>
                        {message.title}
                      </h3>
                      {isUnread && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                          Nova
                        </span>
                      )}
                    </div>
                    
                    <p className={`text-sm mb-2 line-clamp-2 ${isUnread ? 'text-gray-700' : 'text-gray-600'}`}>
                      {message.content}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(message.created_at)}
                        </span>                        <span>
                          De: {message.sender_name || 'Cantina'}
                        </span>
                      </div>
                      
                      {message.read_at && (
                        <span className="flex items-center text-xs text-green-600">
                          <Check className="w-3 h-3 mr-1" />
                          Lida
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <Eye className="w-4 h-4 text-gray-400 ml-4" />
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Modal de visualização da mensagem */}
      {selectedMessage && (
        <MessageModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
        />
      )}
    </div>
  );
};

// Modal para visualizar mensagem completa
const MessageModal = ({ message, onClose }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'normal': return 'Normal';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'normal': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          {/* Header do modal */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <MessageSquare className="w-6 h-6 text-blue-600" />
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(message.priority || 'normal')}`}>
                {getPriorityLabel(message.priority || 'normal')}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Título da mensagem */}
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {message.title}
          </h2>

          {/* Informações do remetente e data */}
          <div className="flex items-center justify-between mb-6 p-3 bg-gray-50 rounded-lg">            <div>
              <p className="text-sm font-medium text-gray-900">
                {message.sender_name || 'Cantina'}
              </p>
              <p className="text-xs text-gray-600">
                {/* Email não está mais disponível via join, pode ser removido ou usar um valor fixo */}
                cantina@sistema.com
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">
                {formatDate(message.created_at)}
              </p>
              {message.recipientData?.read_at && (
                <p className="text-xs text-green-600 flex items-center justify-end">
                  <Check className="w-3 h-3 mr-1" />
                  Lida em {formatDate(message.recipientData.read_at)}
                </p>
              )}
            </div>
          </div>

          {/* Conteúdo da mensagem */}
          <div className="mb-6">
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                {message.content}
              </div>
            </div>
          </div>

          {/* Anexo, se houver */}
          {message.attachment_url && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Anexo</h4>
              <a
                href={message.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <Archive className="w-4 h-4 mr-2" />
                {message.attachment_name || 'Baixar anexo'}
              </a>
            </div>
          )}

          {/* Botão de fechar */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentMessages;
